// createTheme from @mui/material/styles — provides MUI material's palette
// + typography defaults (background.default, theme.typography.h1/h2/...,
// action.*, divider, …) that @mui/material components like Typography
// read at render time. @mui/system's createTheme is too bare and breaks
// Typography. The build splits the `styles` subtree of @mui/material into
// its own `mui-theme` chunk via manualChunks in vite.config.js, so the
// heavy component code (Dialog, Select, Stepper, …) doesn't ride along.
import { createTheme } from '@mui/material/styles';

// Canonical "near-black" text colour used app-wide. Also exposed on the
// palette as `textPrimary.main` so styled-components can reach it via
// `theme.palette.textPrimary.main`. Defining it once here keeps the
// theme file itself DRY (variants + Typography styleOverrides previously
// inlined this literal 17 times).
const TEXT_PRIMARY = 'rgba(34, 34, 34, 1)';

const theme = createTheme({
  palette: {
    primary: {
      main: '#79c912', // Main brand green - hover state for contained buttons + highlights
      salad: 'rgba(185, 248, 51, 1)', // #B9F833 - SALAD LIME, the default fill for every non-transparent button. Used by theme.components.MuiButton variants.
      bright: '#88F200', // Bright green - active states and completed steps (slightly punchier than salad)
      light: 'rgba(121, 201, 18, 0.1)',
      pale: 'rgba(121, 201, 18, 0.05)'
    },
    secondary: {
      main: '#f7d53e', // Golden yellow - secondary actions and accents
      dark: '#d5b216', // Dark yellow - hover states and active elements
      light: 'rgba(247, 213, 62, 0.1)',
      pale: 'rgba(247, 213, 62, 0.05)'
    },
    tertiary: {
      main: '#30b0c7', // Teal blue - tertiary actions and special highlights
      light: 'rgba(48, 176, 199, 0.1)',
      pale: 'rgba(48, 176, 199, 0.05)'
    },
    quaternary: {
      main: '#19857b', // Dark teal - quaternary actions and special elements
      light: 'rgba(25, 133, 123, 0.1)',
      pale: 'rgba(25, 133, 123, 0.05)'
    },
    quinary: {
      main: '#000000', // Pure black - text and high contrast elements
      light: 'rgba(0, 0, 0, 0.1)',
      pale: 'rgba(0, 0, 0, 0.05)'
    },
    error: {
      main: '#d32f2f',
      light: 'rgba(211, 47, 47, 0.1)',
      pale: 'rgba(211, 47, 47, 0.05)'
    },
    // Black colors
    black: {
      main: '#000000', // Pure black
      dark: '#0A0A0A' // Very dark black
    },
    textBlack: {
      main: 'rgba(0, 0, 0, 0.87)' // High-emphasis text black
    },
    textDarkGray: {
      main: '#828282'
    },
    // Grey scale following Material Design
    grey: {
      50: '#F5F5F5', // Lightest (matches your #F5F5F5)
      100: '#ebedee', // Very light (matches your #ebedee)
      200: '#A0A0A0', // Light (matches your #A0A0A0)
      300: '#828282', // Medium-light (matches your #828282)
      400: '#525252', // Medium (matches your #525252)
      500: '#4C4C4C', // Medium-dark (matches your #4C4C4C)
      600: '#141414', // Dark (matches your #141414)
      700: '#0A0A0A', // Darkest (matches your #0A0A0A)
      // 800/900 weren't defined when the theme was hand-rolled; default
      // them to the same darkest tone so any caller that asks for the
      // upper end of the ramp gets a sensible value rather than `undefined`.
      800: '#0A0A0A',
      900: '#000000'
    },

    // White
    white: {
      main: '#FFFFFF'
    },

    purple: {
      // Standalone custom key referenced as theme.palette.purple.main.
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2'
    },

    // Text colors (semantic naming)
    textPrimary: {
      main: 'rgba(34, 34, 34, 1)' // Main text color
    },
    textSecondary: {
      main: '#4C4C4C' // Secondary text
    },
    textTertiary: {
      main: '#828282' // Tertiary text
    },
    textDisabled: {
      main: '#A0A0A0' // Disabled text
    },

    // Background colors
    backgroundLight: {
      main: '#F5F5F5' // Light backgrounds
    },
    backgroundNeutral: {
      main: '#ebedee' // Neutral backgrounds
    }
  },
  textCut: (lines) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    WebkitLineClamp: lines
  }),
  wrapper: {
    maxWidth: 1440,
    margin: '0 auto'
  },
  components: {
    MuiStack: {
      styleOverrides: {
        root: {
          marginBottom: 10,
          gap: 5,
          flexWrap: 'wrap'
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          '& svg': {
            marginRight: 5
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            border: '1px solid #BDBDBD',
            '&.Mui-error': {
              border: '1px solid #d32f2f'
            }
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(185, 248, 51, 1)',
          color: TEXT_PRIMARY,
          backgroundColor: '#fff',
          height: 28,
          '&:hover': {
            backgroundColor: 'rgba(185, 248, 51, 1)',
            color: TEXT_PRIMARY
          }
        },
        label: {
          fontWeight: 500,
          fontSize: 12,
          lineHeight: '12px'
        }
      }
    },
    MuiButton: {
      // Ripple disabled app-wide for buttons (touch + standard).
      defaultProps: {
        disableRipple: true
      },
      // Migrated from MUI v5 `containedPrimary` / `outlinedPrimary` /
      // `sizeSmall` slot keys — those silently break in MUI v6+ (slots
      // removed in favour of the `variants` array below). Previously every
      // contained-primary button was falling back to MUI's plain green
      // because the v5 slot was no longer recognised.
      //
      // Design: the brand "default" button is slightly rounded
      // (borderRadius: 8), pill-size padding, bright-lime fill, dark text.
      // It applies anywhere `<Button variant="contained" color="primary">`
      // is rendered. Pill-shape sites keep their own local overrides
      // (TestStart, ContactCTA, etc.) which only need to bump the
      // borderRadius to 999; everything else inherits cleanly.
      styleOverrides: {
        root: {
          boxShadow: 'none',
          textTransform: 'none',
          fontFamily: 'Rubik-Regular',
          fontSize: 16,
          lineHeight: '20px',
          fontWeight: 500,
          // Global default button text color. Matches palette.textPrimary.main
          // and the contained/outlined variants below. Was inconsistent
          // across the app — Onboarding ButtonRounded forced pure black
          // (`theme.palette.black.main = #000`), while every other button
          // used rgba(34, 34, 34, 1). This anchors the canonical value;
          // per-button overrides (e.g. ButtonStyledWhite using grey[600])
          // still win locally.
          color: TEXT_PRIMARY
        }
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: 'rgba(185, 248, 51, 1)', // primary.bright
            color: TEXT_PRIMARY,
            padding: '12px 22px',
            borderRadius: 8,
            '&:hover': {
              backgroundColor: 'rgba(121, 201, 18, 1)', // primary.main
              color: TEXT_PRIMARY
            }
          }
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            color: TEXT_PRIMARY,
            borderColor: 'rgba(121, 201, 18, 1)', // primary.main
            padding: '11px 21px', // -1px to compensate for the border
            borderRadius: 8,
            '&:hover': {
              backgroundColor: 'rgba(121, 201, 18, 0.08)',
              borderColor: 'rgba(121, 201, 18, 1)'
            }
          }
        },
        {
          props: { size: 'small' },
          style: {
            padding: '8px 16px',
            fontSize: 14,
            lineHeight: '20px'
          }
        }
      ]
    },
    MuiPopper: {
      styleOverrides: {
        paper: {
          fontFamily: 'Rubik-Regular',
          fontSize: 14
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Rubik-Regular'
        },
        h1: {
          fontSize: 36,
          fontWeight: 600,
          fontFamily: 'Unbounded',
          lineHeight: '40px',
          color: TEXT_PRIMARY
        },
        h2: {
          fontSize: 30,
          fontWeight: 600,
          fontFamily: 'Unbounded',
          lineHeight: '45px',
          color: TEXT_PRIMARY
        },
        h3: {
          fontFamily: 'Unbounded',
          fontSize: 21,
          fontWeight: 600,
          lineHeight: '30px',
          color: TEXT_PRIMARY
        },
        body1: {
          fontSize: 18,
          fontWeight: 400,
          lineHeight: '28px',
          color: TEXT_PRIMARY
        },
        body2: {
          fontSize: 16,
          fontWeight: 400,
          lineHeight: '24px',
          color: TEXT_PRIMARY
        },
        subtitle1: {
          fontSize: 18,
          fontWeight: 500,
          lineHeight: '27px',
          color: TEXT_PRIMARY
        },
        subtitle2: {
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
          color: TEXT_PRIMARY
        },
        caption: {
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '21px',
          color: TEXT_PRIMARY,
          marginBottom: 5
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Rubik-Regular',
          fontSize: 14,
          lineHeight: '21px'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontFamily: 'Rubik-Regular',
          fontSize: 16
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: 'Rubik-Regular',
          fontSize: '18px',
          fontWeight: 400,
          lineHeight: '32px',
          textAlign: 'left',
          border: '1px solid rgba(121, 201, 18, 0.2)',
          color: TEXT_PRIMARY,
          backgroundColor: '#fff',
          borderRadius: 8,
          '&.Mui-error': {
            border: '1px solid #d32f2f'
          },
          '@media (max-width: 900px)': {
            fontSize: '16px'
          }
        },
        notchedOutline: {
          border: 'none'
        },
        input: {
          // Symmetric padding + a normal line-height so the text sits
          // vertically centred (the old 10.07px/32px combo pushed it off).
          padding: '13px 14px',
          lineHeight: 1.5
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        // The notched outline is disabled (notchedOutline border:none) and a
        // full-rectangle border lives on the input root, so a shrunk floating
        // label would sit directly on the border line. A white backdrop masks
        // the line behind the label text — recreating the notch.
        outlined: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#fff',
            paddingLeft: 4,
            paddingRight: 4
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: 'Rubik-Regular',
          fontSize: 14,
          lineHeight: '18px',
          marginLeft: 0,
          marginTop: 5
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          minWidth: 200,
          maxWidth: 500,
          backgroundColor: 'rgba(255, 255, 255, 1)',
          padding: 20
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Rubik-Regular'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginTop: 0,
          fontFamily: 'Rubik-Regular'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontFamily: 'Rubik-Regular'
        }
      }
    },
    MuiTab: {
      // Ripple disabled app-wide for tabs.
      defaultProps: {
        disableRipple: true
      }
    }
  }
});

export default theme;
